import Hls, { HlsConfig, ManifestParsedData } from 'hls.js';
import AbstractPlayerEngine from 'components/pages/playback/player-engines/AbstractPlayerEngine';
import { AppData, Router } from '@lightningjs/sdk';
import { Platform } from 'models/platforms/platform';
import NonNativeTextTrackHandler from 'components/pages/playback/player-engines/NonNativeTextTrackHandler';
import { getCommonLanguageCode } from 'support/playerUtils';
import { is1280x720p } from 'support/generalUtils';
import { RoutePath } from 'config/routes';

export default class HlsPlayerEngine extends AbstractPlayerEngine<
  never,
  [url: string]
> {
  private hls: Hls;
  private nonNativeTextTrackHandler: NonNativeTextTrackHandler;
  protected videoElement: HTMLMediaElement;

  constructor(videoElement: HTMLMediaElement, isLiveStream: boolean) {
    super();

    this.videoElement = videoElement;
    this.nonNativeTextTrackHandler = new NonNativeTextTrackHandler(
      videoElement,
    );

    const options: Partial<HlsConfig> = {
      debug: AppData?.debug ?? false,
      autoStartLoad: false, // set to false so we can use startLoad
      maxMaxBufferLength: 10, // default is 600, Setting this to 10 to prevent error when attempting to append buffer to source buffer while buffer is full
      xhrSetup: (xhr, url) => this.augmentXhrRequest(xhr, url),
    };

    this.hls = new Hls(options);
    this.updateOptions(isLiveStream);
  }

  override destroy() {
    super.destroy();

    this.cleanupNonNativeTextTracks();
    this.hls.destroy();
  }

  override async load(startTime: number | undefined, url: string) {
    this.hls.on(
      Hls.Events.MANIFEST_PARSED,
      (eventName: string, manifest: ManifestParsedData) => {
        this.setLiveMaxLatencyDurationCount();

        // Do not render text tracks natively if HLS could not parse any
        // subtitles from the manifest
        const subtitlesAvailable = !!manifest.subtitleTracks.length;
        this.hls.config.renderTextTracksNatively = subtitlesAvailable;

        this.hls.startLoad(startTime ?? 0);

        this.updateSubtitles();
      },
    );

    this.hls.on(Hls.Events.INTERSTITIALS_BUFFERED_TO_BOUNDARY, () => {
      const liveSyncPosition = this.hls.liveSyncPosition;
      if (liveSyncPosition) {
        this.videoElement.currentTime = liveSyncPosition!;
      }
    });

    this.hls.on(Hls.Events.NON_NATIVE_TEXT_TRACKS_FOUND, (_, data) => {
      this.nonNativeTextTrackHandler.addHlsTextTracks(data);
      this.updateSubtitles();
    });

    this.hls.on(Hls.Events.SUBTITLE_TRACKS_UPDATED, () => {
      this.updateSubtitles();
    });

    this.hls.on(Hls.Events.BUFFER_CREATED, () => {
      // There's an issue where changing the audio track before the buffer is created
      // results in a buffer append error
      const audioSetting = AppData!.storageService.audio.get() ?? '';
      this.setAudioTrack(audioSetting);
    });

    this.hls.loadSource(url);
    this.hls.attachMedia(this.videoElement);
  }

  override onAdaption(onAdaptionCallback: (bitrate: number) => void): void {
    this.hls.on(Hls.Events.LEVEL_SWITCHED, (event, { level }) => {
      const newLevel = this.hls.levels[level];
      const bitRate = newLevel!.bitrate / 1000;
      this.currentBitrate = bitRate;
      onAdaptionCallback(bitRate);
    });
  }

  override onError(
    handleError: (isFatal: boolean, errorMessage: string) => void,
  ): void {
    this.hls.on(Hls.Events.ERROR, (event, data) => {
      const { type, details, fatal } = data;
      handleError(fatal, `${type}: ${details}`);
    });
  }

  override getAudioOptions(): string[] {
    const audioTracks: string[] = [];
    this.hls.audioTracks.forEach(track => {
      if (track.lang) audioTracks.push(track.lang);
    });
    return audioTracks;
  }

  override setAudioTrack(lang: string): void {
    const selectedAudioTrack = this.hls.audioTracks.find(
      track => track.lang === lang,
    );
    this.hls.setAudioOption(selectedAudioTrack);
  }

  override getPlayerMedia(): HTMLMediaElement | null {
    return this.videoElement;
  }

  override onUseExternalTextTracks() {
    // Prevents HLS from using the HTML player's TextTracks directly, preventing conflicts
    // between our externally rendered text tracks and HLS' rendering logic
    this.hls.config.renderTextTracksNatively = false;
  }

  override enableCc(lang: string): void {
    if (super.enableCc(lang)) return;

    const commonLanguageCode = getCommonLanguageCode(lang);
    if (this.hls.subtitleTracks?.length) {
      this.enableSubtitleTracksCC(commonLanguageCode);
    } else if (this.nonNativeTextTrackHandler.nonNativeTextTracksAvailable) {
      this.enableNonNativeTextTrack(commonLanguageCode);
    }
  }

  override disableCc() {
    if (super.disableCc()) return;

    this.hls.subtitleDisplay = false;
    this.hls.subtitleTrack = -1;
    this.disableNonNativeTextTracks();
  }

  override extractSubtitleOptions(): string[] {
    if (this.hls.subtitleTracks.length) {
      return this.extractSubtitleTracks();
    } else if (this.nonNativeTextTrackHandler.nonNativeTextTracksAvailable) {
      return this.nonNativeTextTrackHandler.nonNativeTextTrackLanguages;
    } else {
      return [];
    }
  }

  override getLiveSyncPosition(): number | null {
    return this.hls.liveSyncPosition ?? null;
  }

  override getTargetLatency(): number {
    return this.hls.targetLatency ?? 0;
  }

  private extractSubtitleTracks(): string[] {
    const languages = new Set<string>();
    this.hls.subtitleTracks.forEach(track => {
      const { type, lang } = track;
      if (!lang || (type !== 'SUBTITLES' && type !== 'CLOSED-CAPTIONS')) return;

      languages.add(getCommonLanguageCode(lang));
    });

    return Array.from(languages);
  }

  private enableSubtitleTracksCC(lang: string) {
    const subtitleTracks = this.hls.subtitleTracks;
    if (!subtitleTracks?.length) return;

    const trackIndex = subtitleTracks.findIndex(track => {
      const trackLanguage = track.lang;
      if (!trackLanguage) return false;

      return getCommonLanguageCode(trackLanguage) === lang;
    });

    this.hls.subtitleDisplay = trackIndex >= 0;
    this.hls.subtitleTrack = trackIndex;
  }

  private cleanupNonNativeTextTracks() {
    this.disableNonNativeTextTracks();
    this.nonNativeTextTrackHandler.clearNonNativeTextTracks();
  }

  private disableNonNativeTextTracks() {
    this.hls.removeAllListeners(Hls.Events.CUES_PARSED);
    this.nonNativeTextTrackHandler.disableNonNativeTextTrack();
  }

  private enableNonNativeTextTrack(lang: string) {
    const enabledNonNativeTextTrack =
      this.nonNativeTextTrackHandler.enableNonNativeTextTrack(lang);
    if (!enabledNonNativeTextTrack) return;

    this.hls.on(Hls.Events.CUES_PARSED, (_, data) => {
      const { track, cues } = data;
      const reversedCues = this.reverseCues(cues);
      this.nonNativeTextTrackHandler.addNonNativeTextTrackCues(
        reversedCues,
        track,
      );
    });
  }

  // checking to reverse the subtitle lines to fix the order incorrect issue on EL-1278
  // we may need to revert the fixing here in the future once the issue is fixed by player
  private reverseCues(cues: VTTCue[]) {
    // 'channels' (EPG) page only
    if (Router.getActiveRoute() != RoutePath.EPG) return cues;
    // only when the subtitle lines more than one
    if (cues.length < 2) return cues;
    // only when the resolution is 720p
    if (!is1280x720p()) return cues;

    const fontSize = AppData!.device.getCcSettings().fontSize ?? 0;
    // only when the font size is 36px or larger
    if (fontSize < 36) return cues;

    return cues.slice().reverse();
  }

  private setLiveMaxLatencyDurationCount() {
    if (this.hls.url?.includes('hist=')) return;
    /*
      this is how far back we can get behind the live edge
      like liveSyncDurationCount its in increments of EXT-X-TARGETDURATION (about 7 seconds) making sure we don't fall 70 seconds behind
      this allows us to recover from stalling, if we stall for too long we'll jump ahead back to the live edge
    */
    this.hls.config.liveMaxLatencyDurationCount = 10;
  }

  // documentation on HLS options can be found here: https://github.com/video-dev/hls.js/blob/master/docs/API.md
  private updateOptions(isLiveStream: boolean) {
    /*
      this makes sure we don't use levels that have content larger then our player
      this should prevent us from using levels that will take excessively long to load

      This improves the stalling on vizio considerably
    */
    this.hls.config.capLevelToPlayerSize = true;

    /*
      this adds a restriction on capLevelToPlayerSize above so we use only levels for our player size (not defined by our device)
    */
    this.hls.config.ignoreDevicePixelRatio = true;

    /*
      this lowers our level when we have performance issues
      if we're stalling due to performance, we'll switch to a lower level (which should improve performance)
    */
    this.hls.config.capLevelOnFPSDrop = true;

    if (!isLiveStream) return;

    /*
    increasing buffer size, we won't usually have enough content to fill the buffer to this point but having more room will be useful if we need to sync with live playback
    */
    this.hls.config.maxMaxBufferLength = 60;

    /*
    without this setting our back buffer defaults to infinity (and it is up to the player to clear the buffer) this can cause memory issues
    we don't expect to seek backwards, so we keep this length short (it should be non-zero)
    */
    this.hls.config.backBufferLength = 2;

    /*
    this defines how close to the edge of the live content we are
    the increments seem to usually be in 7 seconds (as defined by the EXT-X-TARGETDURATION properties), so we will have a delay of 21 seconds
    this should give us enough time to fill our buffer without stalling
    */
    this.hls.config.liveSyncDurationCount = 3;

    if (AppData!.device.isLowEndPlayerPerformance()) {
      /*
      lowLatencyMode trades latency for stability
      */
      this.hls.config.lowLatencyMode = false;
    }
  }
}
